/**
 * What the buyer reads, and what their card is actually charged.
 *
 * Plans are priced in the currency the business quotes in — mostly USD. HyperPay settles SAR,
 * so the amount is converted to riyals at the point of payment. Both figures are shown, because
 * a card statement in an unexpected currency is a common cause of chargebacks, and a chargeback
 * costs more than the sale and counts against the merchant account.
 *
 * Kept in step with `backend/app/core/currency.py`, which does the conversion that actually
 * charges. This side only displays; if the two ever disagree, the backend is right.
 */

/** The riyal has been pegged to the dollar at this rate since 1986 and held there by SAMA. */
export const SAR_PER_USD = 3.75;

export function usdToSar(usd: number): number {
    return Number((usd * SAR_PER_USD).toFixed(2));
}

export function fmtCurrency(value: number, currency: string | null | undefined): string {
    const c = (currency?.trim() || "USD").toUpperCase();
    const num = Number(value.toFixed(2));
    try {
        return new Intl.NumberFormat("en-US", { style: "currency", currency: c }).format(num);
    } catch {
        return `${c} ${num.toFixed(2)}`;
    }
}

/**
 * `secondary` is null when there is nothing to disclose — the plan is already priced in the
 * currency that will be charged. No other currency is pegged, so converting one from a
 * constant would be wrong rather than approximate.
 */
export function fmtDisplayPrice(
    value: number,
    currency: string | null | undefined,
): { primary: string; secondary: string | null } {
    const c = (currency?.trim() || "USD").toUpperCase();

    if (c === "USD") {
        return { primary: fmtCurrency(value, "USD"), secondary: fmtCurrency(usdToSar(value), "SAR") };
    }

    return { primary: fmtCurrency(value, c), secondary: null };
}

/**
 * Where the API actually lives.
 *
 * Locally and on App Engine the API is same-origin under `/api/v1`, so a bare relative path
 * works. On Render the frontend and API are separate services and nginx does not proxy
 * `/api`, so a relative path is answered by the SPA's own index.html with a 200 — a fetch
 * that parses as a JSON error, and a payment return that silently never reaches the backend.
 * `VITE_API_BASE_URL` is what every other call site already uses; this makes the checkout
 * page and the payment widget use it too.
 */
export const API_V1 = (
    ((typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE_URL) || "/api/v1") as string
).replace(/\/+$/, "");
